import * as THREE from 'three';
import { clamp, damp } from '../util/format';

export type CameraMode = 'chase' | 'high';

const MODES: Record<CameraMode, { back: number; up: number; lookAhead: number; lookUp: number; fov: number }> = {
  chase: { back: 2.65, up: 1.28, lookAhead: 2.8, lookUp: 0.7, fov: 58 },
  high: { back: 5.6, up: 3.05, lookAhead: 5.2, lookUp: 0.45, fov: 70 },
};

export class ChaseCamera {
  readonly camera: THREE.PerspectiveCamera;
  private readonly desired = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private readonly lookDesired = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  private readonly offset = new THREE.Vector3();
  private shake = 0;
  private ready = false;
  mode: CameraMode = 'chase';
  shakeEnabled = true;
  private readonly raycaster = new THREE.Raycaster();

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(62, aspect, 0.12, 2500);
  }

  snap(target: THREE.Vector3, quaternion: THREE.Quaternion): void {
    this.pose(target, quaternion, 16, 1);
    this.camera.position.copy(this.desired);
    this.look.copy(this.lookDesired);
    this.camera.lookAt(this.look);
    this.ready = true;
  }

  addShake(amount: number): void {
    this.shake = Math.min(1.2, this.shake + amount);
  }

  update(
    dt: number,
    target: THREE.Vector3,
    quaternion: THREE.Quaternion,
    speed: number,
    blockers: THREE.Object3D | null,
  ): void {
    this.pose(target, quaternion, speed, dt);
    if (!this.ready) {
      this.snap(target, quaternion);
      return;
    }
    const k = 1 - Math.exp(-4.2 * dt);
    this.camera.position.lerp(this.desired, k);
    this.look.lerp(this.lookDesired, 1 - Math.exp(-6 * dt));

    if (blockers) {
      const direction = this.offset.copy(this.camera.position).sub(target);
      const distance = direction.length();
      if (distance > 0.2) {
        direction.multiplyScalar(1 / distance);
        this.raycaster.set(target, direction);
        this.raycaster.far = distance;
        this.raycaster.near = 0.3;
        const hits = this.raycaster.intersectObject(blockers, true);
        if (hits.length > 0 && hits[0].distance < distance - 0.25) {
          this.camera.position.copy(target).addScaledVector(direction, Math.max(0.7, hits[0].distance - 0.3));
        }
      }
    }

    if (this.shakeEnabled && this.shake > 0.01) {
      const time = performance.now() * 0.02;
      this.camera.position.x += Math.sin(time * 1.7) * this.shake * 0.08;
      this.camera.position.y += Math.cos(time * 2.1) * this.shake * 0.05;
      this.shake = damp(this.shake, 0, dt, 3.5);
    } else if (this.shakeEnabled && Math.abs(speed) > 12) {
      const time = performance.now() * 0.01;
      this.camera.position.y += Math.sin(time * 3) * 0.012;
    }

    const spec = MODES[this.mode];
    const fov = spec.fov + clamp(Math.abs(speed) / 20, 0, 1) * 7;
    this.camera.fov = damp(this.camera.fov, fov, dt, 4);
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(this.look);
  }

  private pose(target: THREE.Vector3, quaternion: THREE.Quaternion, speed: number, _dt: number): void {
    const spec = MODES[this.mode];
    this.forward.set(0, 0, 1).applyQuaternion(quaternion);
    this.forward.y *= 0.35;
    if (this.forward.lengthSq() < 1e-4) this.forward.set(0, 0, 1);
    this.forward.normalize();
    const speedBoost = clamp(Math.abs(speed) / 18, 0, 1);
    this.desired.copy(target)
      .addScaledVector(this.forward, -(spec.back + speedBoost * 0.7))
      .add(new THREE.Vector3(0, spec.up + speedBoost * 0.15, 0));
    this.lookDesired.copy(target)
      .addScaledVector(this.forward, spec.lookAhead + speedBoost * 1.4)
      .add(new THREE.Vector3(0, spec.lookUp, 0));
  }

  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }
}
